import React from 'react';
import { AddProps } from './interfaces';
import { useAddController } from './add.controller';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  IconButton,
  Input,
  Select,
  Stack,
  Text,
  Textarea,
} from '@chakra-ui/react';
import BenefitPreviewer from '../../../../../components/BenefitPreviewer';
import ImageCropField from '../../../../../components/ImageCropField';
import BikletaPointsCost from '../../../../../components/BikletaPointsCost';
import BenefitTypeSelector from '../../../../../components/BenefitTypeSelector';
import BenefitTypeDetail from '../../../../../components/BenefitTypeDetail';
import { Field, FormikProvider, getIn } from 'formik';
import { ArrowBackIcon } from '@chakra-ui/icons';
import { colors } from '../../../../../utils/colors';
import { useNavigate } from 'react-router-dom';

import {
  stateOptions as statusOptions,
  typeOptions,
} from '../../../../../utils/constants';
import { format } from 'date-fns';

export const AddView: React.FC<AddProps> = ({
  useController = useAddController,
}) => {
  const controller = useController();

  const navigate = useNavigate();

  return (
    <FormikProvider value={controller.formikInstance}>
      <Stack direction={'row'} alignItems={'center'} width={'100%'} px={2}>
        <IconButton
          icon={<ArrowBackIcon boxSize={'1.5rem'} />}
          aria-label="backIconButton"
          color={colors.gray[0]}
          onClick={() => navigate('/admin/benefits')}
          variant={'ghost'}
        />
        <Stack direction={'column'} w={'100%'}>
          <Box>
            <Text fontSize={24} fontWeight={'700'} color={colors.gray[0]}>
              Añadir beneficio
            </Text>
          </Box>
        </Stack>
      </Stack>
      <Box px={3}>
        <Divider backgroundColor={'white'} mt={2} mb={6} w={'100%'} />
      </Box>
      <Stack
        direction={{
          lg: 'row',
          sm: 'column',
        }}
        spacing={'10'}
        h={['67vh', 'auto', 'auto']}
        w={'100%'}
        px={[2, 20]}
        mt={'10'}>
        <Stack direction={'column'} w={['auto', '50%', '85%']}>
          <FormControl
            isInvalid={getIn(controller.formikInstance.errors, 'title')}
            isRequired
            variant={'floating'}>
            <Field name="title">
              {({ field }) => (
                <Input
                  {...field}
                  type="text"
                  placeholder="Título"
                  size={'md'}
                  value={controller.capitalizeFirstLetter(field?.value)}
                />
              )}
            </Field>
            <FormLabel>Titulo</FormLabel>

            {getIn(controller.formikInstance.errors, 'title') ? (
              <FormErrorMessage>
                {getIn(controller.formikInstance.errors, 'title')}
              </FormErrorMessage>
            ) : (
              <FormHelperText userSelect={'none'} color={'transparent'}>
                Por favor, ingresa un titulo
              </FormHelperText>
            )}
          </FormControl>

          <FormControl
            isInvalid={getIn(controller.formikInstance.errors, 'description')}
            isRequired
            variant={'floating'}>
            <Field name="description">
              {({ field }) => (
                <Textarea
                  {...field}
                  resize={'none'}
                  type="text"
                  placeholder="Descripción del beneficio"
                  height={'160px'}
                  value={controller.capitalizeFirstLetter(field?.value)}
                />
              )}
            </Field>
            <FormLabel>Descripción del beneficio</FormLabel>
            {getIn(controller.formikInstance.errors, 'description') ? (
              <FormErrorMessage>
                {getIn(controller.formikInstance.errors, 'description')}
              </FormErrorMessage>
            ) : (
              <FormHelperText userSelect={'none'} color={'transparent'}>
                Por favor, ingresa una descripción
              </FormHelperText>
            )}
          </FormControl>
          <Stack
            direction={{
              lg: 'row',
              md: 'column',
              sm: 'column',
            }}
            spacing={4}>
            <FormControl
              isInvalid={getIn(controller.formikInstance.errors, 'status')}
              isRequired
              variant={'floating'}>
              <Field name="status">
                {({ field }) => (
                  <Select {...field} placeholder="" size={'md'}>
                    {statusOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <FormLabel>Estado</FormLabel>
              {getIn(controller.formikInstance.errors, 'status') ? (
                <FormErrorMessage noOfLines={1}>
                  {getIn(controller.formikInstance.errors, 'status')}
                </FormErrorMessage>
              ) : (
                <FormHelperText userSelect={'none'} color={'transparent'}>
                  Elige un estado
                </FormHelperText>
              )}
            </FormControl>

            <FormControl
              isInvalid={getIn(controller.formikInstance.errors, 'startDate')}
              isRequired>
              <Field name="startDate">
                {({ field }) => (
                  <Input
                    {...field}
                    type="date"
                    placeholder="Fecha de inicio"
                    size={'md'}
                    min={format(new Date(), 'yyyy-MM-dd')}
                    maxLength={10}
                    onChange={event => {
                      if (event.target.value.length <= 10) {
                        controller.formikInstance.setFieldValue(
                          'startDate',
                          event.target.value
                        );
                      }
                    }}
                  />
                )}
              </Field>
              {getIn(controller.formikInstance.errors, 'startDate') ? (
                <FormErrorMessage>
                  {getIn(controller.formikInstance.errors, 'startDate')}
                </FormErrorMessage>
              ) : (
                <FormHelperText userSelect={'none'} color={'transparent'}>
                  Ingrese Fecha de inicio
                </FormHelperText>
              )}
            </FormControl>

            <FormControl
              isInvalid={getIn(controller.formikInstance.errors, 'endDate')}
              isRequired>
              <Field name="endDate">
                {({ field }) => (
                  <Input
                    {...field}
                    type="date"
                    placeholder="Fecha de fin"
                    size={'md'}
                    maxLength={10}
                    min={
                      controller.formikInstance.values.startDate ||
                      format(new Date(), 'yyyy-MM-dd')
                    }
                    onChange={event => {
                      if (event.target.value.length <= 10) {
                        controller.formikInstance.setFieldValue(
                          'endDate',
                          event.target.value
                        );
                      }
                    }}
                  />
                )}
              </Field>
              {getIn(controller.formikInstance.errors, 'endDate') ? (
                <FormErrorMessage>
                  {getIn(controller.formikInstance.errors, 'endDate')}
                </FormErrorMessage>
              ) : (
                <FormHelperText userSelect={'none'} color={'transparent'}>
                  Ingrese Fecha de fin
                </FormHelperText>
              )}
            </FormControl>
          </Stack>

          <Stack direction={'column'} spacing={2}>
            <Text
              fontSize={11}
              fontWeight={500}
              color={'rgba(255,255,255,0.4)'}
              textTransform={'uppercase'}
              letterSpacing={'0.6px'}>
              Tipo de beneficio{' '}
              <Text as={'span'} color={colors.accent.main}>
                *
              </Text>
            </Text>
            <BenefitTypeSelector
              value={controller.formikInstance.values.type}
              onChange={value => {
                controller.formikInstance.setFieldValue('type', value);
                controller.formikInstance.setFieldValue('benefitTag', '');
              }}
              options={typeOptions}
            />
            {getIn(controller.formikInstance.errors, 'type') && (
              <Text fontSize={12} color={colors.red.main}>
                {getIn(controller.formikInstance.errors, 'type')}
              </Text>
            )}

            <BenefitTypeDetail
              type={controller.formikInstance.values.type}
              value={controller.formikInstance.values.benefitTag}
              onChange={value =>
                controller.formikInstance.setFieldValue('benefitTag', value)
              }
              error={
                getIn(
                  controller.formikInstance.errors,
                  'benefitTag'
                ) as string
              }
            />
          </Stack>

          <Stack>
            <FormControl
              isInvalid={getIn(
                controller.formikInstance.errors,
                'unlimitedQuantity'
              )}
              isRequired>
              <Field name="unlimitedQuantity">
                {({ field }) => (
                  <Checkbox colorScheme={'brand'} {...field}>
                    Ilimitado
                  </Checkbox>
                )}
              </Field>
              <FormErrorMessage>
                {getIn(controller.formikInstance.errors, 'unlimitedQuantity')}
              </FormErrorMessage>
            </FormControl>
            <FormControl
              isInvalid={getIn(
                controller.formikInstance.errors,
                'availableQuantity'
              )}
              variant={'floating'}>
              <Field name="availableQuantity">
                {({ field, form }) => (
                  <Input
                    {...field}
                    onChange={event => {
                      const parsedValueNumber = Number(event.target.value);

                      form.setFieldValue(
                        'availableQuantity',
                        isNaN(parsedValueNumber) ? 1 : parsedValueNumber
                      );
                    }}
                    placeholder=""
                    disabled={form.values.unlimitedQuantity === true}
                    maxLength={9}
                  />
                )}
              </Field>
              <FormLabel>Cantidad en stock</FormLabel>
              {getIn(controller.formikInstance.errors, 'availableQuantity') ? (
                <FormErrorMessage>
                  {getIn(controller.formikInstance.errors, 'availableQuantity')}
                </FormErrorMessage>
              ) : (
                <FormHelperText userSelect={'none'} color={'transparent'}>
                  Por favor, indique una cantidad
                </FormHelperText>
              )}
            </FormControl>
          </Stack>

          <Box px={1} py={2}>
            <Divider backgroundColor={'white'} opacity={0.08} />
          </Box>

          <BikletaPointsCost
            value={controller.formikInstance.values.pointsCost}
            onChange={value =>
              controller.formikInstance.setFieldValue('pointsCost', value)
            }
            error={
              getIn(controller.formikInstance.errors, 'pointsCost') as string
            }
          />

          <FormControl
            isInvalid={getIn(controller.formikInstance.errors, 'bannerFile')}
            isRequired>
            <FormLabel fontSize={16} fontWeight={'400'}>
              Añadir foto Home Beneficios <b>560x350</b>
            </FormLabel>
            <ImageCropField
              outputWidth={560}
              outputHeight={350}
              maxFileSizeMB={10}
              fileName="banner.jpg"
              onChange={file =>
                controller.formikInstance.setFieldValue('bannerFile', file)
              }
            />
            {getIn(controller.formikInstance.errors, 'bannerFile') ? (
              <FormErrorMessage>
                {getIn(controller.formikInstance.errors, 'bannerFile')}
              </FormErrorMessage>
            ) : (
              <FormHelperText userSelect={'none'} color={'transparent'}>
                Por favor, suba una imagen de banner
              </FormHelperText>
            )}
          </FormControl>
          <FormControl
            isInvalid={getIn(controller.formikInstance.errors, 'listFile')}
            isRequired>
            <FormLabel fontSize={16} fontWeight={'400'}>
              Añadir foto Lista Beneficios <b>1200x600</b>
            </FormLabel>
            <ImageCropField
              outputWidth={1200}
              outputHeight={600}
              maxFileSizeMB={10}
              fileName="list.jpg"
              onChange={file =>
                controller.formikInstance.setFieldValue('listFile', file)
              }
            />
            {getIn(controller.formikInstance.errors, 'listFile') ? (
              <FormErrorMessage>
                {getIn(controller.formikInstance.errors, 'listFile')}
              </FormErrorMessage>
            ) : (
              <FormHelperText userSelect={'none'} color={'transparent'}>
                Por favor, suba una imagen de lista
              </FormHelperText>
            )}
          </FormControl>
          <FormControl
            isInvalid={getIn(controller.formikInstance.errors, 'coverFile')}
            isRequired>
            <FormLabel fontSize={16} fontWeight={'600'}>
              Añadir foto Detalle Beneficio <b>1320x1100</b>
            </FormLabel>
            <ImageCropField
              outputWidth={1320}
              outputHeight={1100}
              maxFileSizeMB={50}
              fileName="cover.jpg"
              onChange={file =>
                controller.formikInstance.setFieldValue('coverFile', file)
              }
            />
            {getIn(controller.formikInstance.errors, 'coverFile') ? (
              <FormErrorMessage>
                {getIn(controller.formikInstance.errors, 'coverFile')}
              </FormErrorMessage>
            ) : (
              <FormHelperText userSelect={'none'} color={'transparent'}>
                Por favor, suba una imagen de portada
              </FormHelperText>
            )}
          </FormControl>
        </Stack>

        <Button
          display={{ base: 'flex', sm: 'flex', lg: 'none' }}
          type="submit"
          onClick={controller.formikInstance.submitForm}
          backgroundColor={'#FFC700'}
          fontSize={16}
          lineHeight={'24px'}
          fontWeight={'700'}
          color={'#363449'}
          borderRadius={100}
          p={'16px 32px'}
          gap={'8px'}
          _hover={{ bgColor: '#FFD84E' }}
          _focus={{ bgColor: '#FFED90' }}
          isLoading={controller.formikInstance.isSubmitting}
          w={'206px'}
          h={'56px'}>
          Crear beneficio
        </Button>

        <Stack
          h={'inherit'}
          display={{
            lg: 'flex',
            sm: 'none',
          }}
          w={'100%'}>
          <Box
            w={'100%'}
            h={'full'}
            display={'flex'}
            justifyContent={'flex-end'}>
            <Box>
              <Text
                borderWidth={2}
                px={3}
                py={1}
                borderRadius={8}
                maxWidth={'fit-content'}
                fontSize={16}
                fontWeight={'600'}
                lineHeight={5}
                padding={'8px 16px'}>
                Previsualización
              </Text>
              <Box mt={4}>
                <BenefitPreviewer
                  benefitObject={controller.formikInstance?.values}
                  formikContext={controller.formikInstance}
                />
                <Box
                  w={'100%'}
                  display={'flex'}
                  justifyContent={'flex-start'}
                  mt={8}>
                  <Button
                    display={['none', 'none', 'block']}
                    type="submit"
                    onClick={controller.formikInstance.submitForm}
                    backgroundColor={'#FFC700'}
                    fontSize={16}
                    lineHeight={'24px'}
                    fontWeight={'700'}
                    color={'#363449'}
                    borderRadius={100}
                    p={'16px 32px'}
                    gap={'8px'}
                    _hover={{ bgColor: '#FFD84E' }}
                    _focus={{ bgColor: '#FFED90' }}
                    isLoading={controller.formikInstance.isSubmitting}
                    w={'206px'}
                    h={'56px'}>
                    Crear beneficio
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        </Stack>
      </Stack>
    </FormikProvider>
  );
};
